import React from 'react'

// Canonical caption engines — kept in lock-step with the main site
// (frontend-web/web/templates/index_3_0.html) so the Studio burns exactly what
// the dashboard previews. Values are the keys the backend caption engine accepts.
export type CaptionStyle =
  | 'dynamic_viral'
  | 'bold_impact'
  | 'youtube_shorts'
  | 'instagram_reels'
  | 'hype_beast'
  | 'educational'
  | 'professional_clean'
  | 'minimal_pro'

export interface CaptionStyleSpec {
  value: CaptionStyle
  label: string   // emoji + name, matches the site
  short: string   // compact name for the header chip
  desc: string    // what it means / when to use it
  // Render spec (hex without '#'), mirrors the site's live preview.
  text: string
  out: string
  ow: number
  accent: string
  caps: boolean
  font: string
}

// Specs mirror the burn engine (worker legit2clip/captions.py) 1:1 — colours,
// casing and font weight match what actually renders, so the preview never lies.
export const CAPTION_STYLES: CaptionStyleSpec[] = [
  { value: 'dynamic_viral', label: '🔥 Dynamic Viral', short: 'Dynamic Viral',
    desc: 'Cyan keyword with a glowing pop — the all-round viral look',
    text: 'FFFFFF', out: '000000', ow: 3, accent: '00E5FF', caps: true, font: "Impact,'Arial Narrow',sans-serif" },
  { value: 'bold_impact', label: '🎬 Bold Impact', short: 'Bold Impact',
    desc: 'Huge yellow caps, white keyword — MrBeast-style slam',
    text: 'FFE400', out: '000000', ow: 4, accent: 'FFFFFF', caps: true, font: "'Arial Black',sans-serif" },
  { value: 'youtube_shorts', label: '▶️ YouTube Shorts', short: 'YouTube Shorts',
    desc: 'YouTube-red keyword, crisp caps — no glow',
    text: 'FFFFFF', out: '000000', ow: 3, accent: 'FF0033', caps: true, font: "Impact,'Arial Narrow',sans-serif" },
  { value: 'instagram_reels', label: '📸 Instagram Reels', short: 'Instagram Reels',
    desc: 'Magenta keyword with a soft glow — Reels energy',
    text: 'FFFFFF', out: '000000', ow: 3, accent: 'FF2D95', caps: true, font: "Impact,'Arial Narrow',sans-serif" },
  { value: 'hype_beast', label: '🏆 Hype Beast', short: 'Hype Beast',
    desc: 'Yellow caps on a red outline — maximum hype',
    text: 'FFE400', out: 'FF0000', ow: 4, accent: 'FFFFFF', caps: true, font: "'Arial Black',sans-serif" },
  { value: 'educational', label: '📚 Educational', short: 'Educational',
    desc: 'Sentence case, blue highlighted keyword — clean explainer',
    text: 'FFFFFF', out: '000000', ow: 2, accent: '2E7DFF', caps: false, font: "'Segoe UI',system-ui,sans-serif" },
  { value: 'professional_clean', label: '💼 Professional', short: 'Professional',
    desc: 'Sentence case, gold key-words — restrained lower-third',
    text: 'FFFFFF', out: '000000', ow: 2, accent: 'F2B01E', caps: false, font: "'Segoe UI',system-ui,sans-serif" },
  { value: 'minimal_pro', label: '✨ Minimal Pro', short: 'Minimal Pro',
    desc: 'Sentence case, understated white — barely-there',
    text: 'FFFFFF', out: '000000', ow: 2, accent: 'E5E7EB', caps: false, font: "'Segoe UI',system-ui,sans-serif" },
]

export const DEFAULT_CAPTION_STYLE: CaptionStyle = 'dynamic_viral'

export function captionStyleSpec(value?: CaptionStyle): CaptionStyleSpec {
  return CAPTION_STYLES.find(s => s.value === value) || CAPTION_STYLES[0]
}

/** Live "This changed EVERYTHING" preview, replicating the site's caption look
 *  (accent keyword + text stroke) so users see exactly what will be burned in. */
export function CaptionPreview({ spec, size = 13 }: { spec: CaptionStyleSpec; size?: number }) {
  const t = spec.caps ? 'THIS CHANGED EVERYTHING' : 'This changed EVERYTHING'
  const kw = 'EVERYTHING'
  const i = t.indexOf(kw)
  const style: React.CSSProperties = {
    fontFamily: spec.font,
    fontWeight: 900,
    color: `#${spec.text}`,
    fontSize: size,
    lineHeight: 1.12,
    textAlign: 'center',
    WebkitTextStroke: `${spec.ow}px #${spec.out}`,
    paintOrder: 'stroke fill',
    textShadow: '0 2px 5px rgba(0,0,0,.6)',
  } as React.CSSProperties
  return (
    <div style={style}>
      {t.slice(0, i)}
      <span style={{ color: `#${spec.accent}` }}>{kw}</span>
      {t.slice(i + kw.length)}
    </div>
  )
}
